/**
 * License management for OmegaSynth
 * Wraps @astermindai/license-runtime with convenience functions
 */

import { 
  initLicenseRuntime, 
  setLicenseToken, 
  requireFeature, 
  hasFeature, 
  getLicenseState,
  type LicenseState 
} from '@astermindai/license-runtime';

let initialized = false;

/**
 * Initialize the license runtime singleton.
 * Must be called before any other license functions.
 * Automatically loads token from ASTERMIND_LICENSE_TOKEN environment variable if present.
 * 
 * PROFESSIONAL LICENSING APPROACH:
 * - Always requires a valid license key (strict mode)
 * - Trial keys are obtained from the license server and have expiration dates
 * - No "eval mode" bypass - all usage requires a valid key
 * - For testing, use a test/dev key or mock the license runtime
 * 
 * This follows industry best practices where:
 * 1. All users (including evaluators) must obtain a trial key
 * 2. License validation happens server-side via JWT verification
 * 3. Trial keys expire after a set period (e.g., 30 days)
 * 4. No environment-based bypasses are possible
 */
export function initializeLicense(): void {
  if (initialized) {
    return; // Already initialized
  }

  // Always use strict mode - require valid license key
  // Trial keys are just license keys with expiration dates
  initLicenseRuntime({
    jwksUrl: "https://license.astermind.ai/.well-known/astermind-license-keys.json",
    expectedIss: "https://license.astermind.ai",
    expectedAud: "astermind-synth",
    jwksMaxAgeSeconds: 300,
    mode: 'strict' // Always strict - no bypasses
  });

  // Load token from environment variable if present
  const token = process.env.ASTERMIND_LICENSE_TOKEN;
  if (token) {
    // Note: setLicenseToken is async, but we can't await in module initialization
    // The token will be set asynchronously
    setLicenseToken(token).catch(err => {
      console.warn("Failed to set license token from environment:", err);
    });
  }

  initialized = true;
}

/**
 * Require a valid license before proceeding.
 * Throws if license is invalid, expired, or feature is missing.
 * 
 * SECURITY: This function ONLY trusts payloads that have been verified by the license-runtime.
 * We never decode or trust unverified JWT payloads to prevent bypass attacks.
 * 
 * FLEXIBLE AUDIENCE CHECK:
 * - If the token's audience matches "astermind-synth", it passes normally
 * - If the token's audience doesn't match but the token includes the "astermind-synth" feature,
 *   it will still pass (allows tokens with audience "astermind-elm" or other products that include this feature)
 * - This flexibility ONLY applies when the license-runtime has VERIFIED the token signature
 * 
 * All users (including evaluators) must obtain a trial or production license key.
 * Trial keys can be obtained from: https://license.astermind.ai/v1/trial/create
 */

/**
 * Check if the token has a valid feature for astermind-synth.
 * Accepts both "astermind-synth" and "astermind-elm-basic" features.
 * 
 * SECURITY: Only call this with verified payloads from license-runtime state.
 */
function hasValidFeature(features: string[]): boolean {
  return Array.isArray(features) && (
    features.includes("astermind-synth") || 
    features.includes("astermind-elm-basic")
  );
}

export function requireLicense(): void {
  try {
    requireFeature("astermind-synth");
  } catch (error: any) {
    const state = getLicenseState();
    
    // SECURITY: Only trust payloads that have been verified by license-runtime.
    // If state.payload exists, it means the license-runtime has successfully verified
    // the JWT signature. We can safely check features in verified payloads.
    if (state.payload && hasValidFeature(state.payload.features)) {
      // Feature is present in VERIFIED payload, even if audience doesn't match - allow it
      // This handles tokens with audience "astermind-elm" that include "astermind-synth" feature
      return;
    }
    
    // Handle "missing" state: Token not set or async setLicenseToken() hasn't completed
    if (state.status === 'missing') {
      // SECURITY: Do NOT decode unverified tokens. Wait for license-runtime verification.
      // If token exists in env but state is "missing", it means async verification hasn't completed.
      // We must wait for verification - do not trust unverified payloads.
      const token = process.env.ASTERMIND_LICENSE_TOKEN;
      if (token) {
        // Token exists but not verified yet - give a helpful error message
        throw new Error(
          'License token is being verified. Please wait a moment and try again.\n' +
          'If this error persists, verify your license token is valid.\n' +
          'For trial tokens, visit: https://license.astermind.ai/v1/trial/create'
        );
      }
      throw new Error(
        'License token is required. Please set ASTERMIND_LICENSE_TOKEN environment variable.\n' +
        'For trial tokens, visit: https://license.astermind.ai/v1/trial/create'
      );
    } else if (state.status === 'expired') {
      throw new Error(
        `License token has expired. Please obtain a new license token.\n` +
        `Expired at: ${state.payload?.exp ? new Date(state.payload.exp * 1000).toISOString() : 'unknown'}`
      );
    } else if (state.status === 'invalid') {
      // SECURITY: Do NOT decode unverified tokens. If state is "invalid", the token failed verification.
      // This could be due to:
      // - Invalid signature (token was tampered with or is fake)
      // - Wrong issuer
      // - Other verification failures
      // We must reject invalid tokens - never trust unverified payloads.
      throw new Error(
        `License token is invalid: ${state.reason || 'unknown error'}\n` +
        'The token failed cryptographic verification. Please verify your license token is correct.\n' +
        'For trial tokens, visit: https://license.astermind.ai/v1/trial/create'
      );
    }
    
    // Re-throw original error if we can't provide better message
    throw error;
  }
}

/**
 * Check if license is valid and feature is available (non-blocking).
 * @returns true if astermind-synth feature is available
 */
export function checkLicense(): boolean {
  return hasFeature("astermind-synth");
}

/**
 * Get detailed license status.
 * @returns LicenseState object with status, reason, payload, etc.
 */
export function getLicenseStatus(): LicenseState {
  return getLicenseState();
}

/**
 * Set license token from a string.
 * Useful for dynamic token loading from backend services or user input.
 * @param token The license token string (JWT format)
 */
export async function setLicenseTokenFromString(token: string): Promise<void> {
  await setLicenseToken(token);
}

/**
 * Check if license runtime has been initialized.
 * @returns true if initializeLicense() has been called
 */
export function isLicenseInitialized(): boolean {
  return initialized;
}

